import Command from '@ckeditor/ckeditor5-core/src/command'
import findAttributeRange from '@ckeditor/ckeditor5-typing/src/utils/findattributerange'
import Range from '@ckeditor/ckeditor5-engine/src/model/range'
import toMap from '@ckeditor/ckeditor5-utils/src/tomap'
import Schema from '@ckeditor/ckeditor5-engine/src/model/schema'
import Element from '@ckeditor/ckeditor5-engine/src/model/element'

export class AnchorCommand extends Command {
  declare value: string | undefined

  execute(id: string): void {
    const model = this.editor.model
    const selection = model.document.selection

    model.change((writer) => {
      // If selection is collapsed then update selected anchor or insert new one at the place of caret.
      if (selection.isCollapsed) {
        const position = selection.getFirstPosition()
        const selectionAnchorId = selection.getAttribute('anchorId')

        // When selection is inside text with `anchorId` attribute.
        if (typeof selectionAnchorId === 'string' && position) {
          // Then update `anchorId` value.
          const anchorRange = findAttributeRange(position, 'anchorId', selectionAnchorId, model)

          writer.setAttribute('anchorId', id, anchorRange)

          // Put the selection at the end of the updated anchor.
          const endOfRange = anchorRange.end.nodeBefore
          if (endOfRange) {
            writer.setSelection(writer.createPositionAfter(endOfRange))
          }
        }
        // If not then insert text node with `anchorId` attribute in place of caret.
        // However, since selection in collapsed, attribute value will be used as data for text node.
        // So, if `id` is empty, do not create text node.
        else if (id !== '') {
          const attributes = toMap(Array.from(selection.getAttributes()))

          attributes.set('anchorId', id)

          const { end: positionAfter } = model.insertContent(
            writer.createText(id, Object.fromEntries(attributes.entries())),
            position,
          )

          // Put the selection at the end of the inserted anchor.
          // Using end of range returned from insertContent in case nodes with the same attributes got merged.
          writer.setSelection(positionAfter)
        }
      } else {
        // If selection has non-collapsed ranges, we change attribute on nodes inside those ranges
        // omitting nodes where the `anchorId` attribute is disallowed.
        const ranges = model.schema.getValidRanges(Array.from(selection.getRanges()), 'anchorId')

        // But for the first, check whether the `anchorId` attribute is allowed on selected blocks (e.g. the "image" element).
        const allowedRanges = []

        for (const element of selection.getSelectedBlocks()) {
          if (model.schema.checkAttribute(element, 'anchorId')) {
            allowedRanges.push(writer.createRangeOn(element))
          }
        }

        // Ranges that accept the `anchorId` attribute. Since we will iterate over `allowedRanges`, let's clone it.
        const rangesToUpdate = allowedRanges.slice()

        // For all selection ranges we want to check whether given range is inside an element that accepts the `anchorId` attribute.
        // If so, we don't want to propagate applying the attribute to its children.
        for (const range of ranges) {
          if (this._isRangeToUpdate(range, allowedRanges)) {
            rangesToUpdate.push(range)
          }
        }

        for (const range of rangesToUpdate) {
          writer.setAttribute('anchorId', id, range)
        }
      }
    })
  }

  refresh(): void {
    const model = this.editor.model
    const doc = model.document

    const anchorId =
      doc.selection.getAttribute('anchorId') ?? doc.selection.getSelectedElement()?.getAttribute('anchorId')
    this.value = typeof anchorId === 'string' ? anchorId : undefined
    this.isEnabled = model.schema.checkAttributeInSelection(doc.selection, 'anchorId')
  }

  private _isRangeToUpdate(range: Range, allowedRanges: Range[]): boolean {
    for (const allowedRange of allowedRanges) {
      // A range is inside an element that will have the `anchorId` attribute. Do not modify its nodes.
      if (allowedRange.containsRange(range)) {
        return false
      }
    }

    return true
  }
}

export function isImageAllowed(element: Element | null, schema: Schema): element is Element {
  if (!element) {
    return false
  }

  return element.is('element', 'image') && schema.checkAttribute('image', 'anchorId')
}
